import React, { useState, useEffect } from 'react';
import { X, Building2, User, MapPin, FileText } from 'lucide-react';
import { $api } from '../../api';
import ErrorMessage from '../ErrorMessage';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import countries from './countries';

interface Entity {
  id: string;
  name: string;
  kind: 'company' | 'person';
  email?: string;
}

interface AddEntityModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: 'entreprise' | 'personne';
  onSubmit: () => void;
  editingEntity?: Entity | null;
  isEditMode?: boolean;
  organizationId: string;
}

const AddEntityModal: React.FC<AddEntityModalProps> = ({ 
  isOpen, 
  onClose, 
  entityType, 
  onSubmit, 
  editingEntity, 
  isEditMode = false,
  organizationId
}) => {
  const [formData, setFormData] = useState({
    // Entreprise fields
    companyName: '',
    country: '',
    bceNumber: '',
    // Personne physique fields
    firstName: '',
    lastName: '',
    birthDate: '',
    gender: '',
    birthPlace: '',
    personCountry: '',
    // Common fields
    email: '',
    phone: '',
    // Address fields
    addressLine1: '',
    addressLine2: '',
    city: '',
    stateProvince: '',
    postalCode: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'address'>('info');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Create company mutation
  const createCompanyMutation = $api.useMutation('post', '/api/organization/{organization_id}/entities/companies');
  
  // Create person mutation
  const createPersonMutation = $api.useMutation('post', '/api/organization/{organization_id}/entities/persons');

  // Update entity mutation (for both companies and persons)
  const updateEntityMutation = $api.useMutation('patch', '/api/organization/{organization_id}/entities/{id}');

  // Fetch entity details when in edit mode
  const entityDetailsQuery = $api.useQuery(
    'get',
    '/api/organization/{organization_id}/entities/{id}',
    {
      queryKey: ['entity', editingEntity?.id],
      params: {
        path: {
          organization_id: organizationId,
          id: editingEntity?.id || '',
        },
      },
    },
    {
      enabled: isEditMode && !!editingEntity?.id && isOpen,
      refetchOnMount: true, // Always refetch when component mounts if query is stale
      staleTime: 0, // Consider data stale immediately so it refetches after invalidation
    }
  );

  // Populate form when entity details are loaded or when editing
  useEffect(() => {
    if (isEditMode && editingEntity) {
      if (entityDetailsQuery.data) {
        const entity = entityDetailsQuery.data;
        
        if (entity.kind === 'company') {
          setFormData({
            companyName: entity.name || '',
            country: entity.country || '',
            bceNumber: entity.enterprise_number || '',
            firstName: '',
            lastName: '',
            birthDate: '',
            gender: '',
            birthPlace: '',
            personCountry: '',
            email: entity.email || '',
            phone: entity.phone || '',
            addressLine1: entity.address_line1 || '',
            addressLine2: entity.address_line2 || '',
            city: entity.city || '',
            stateProvince: entity.state_province || '',
            postalCode: entity.postal_code || ''
          });
        } else {
          // Map gender from API format to form format
          const genderMap: Record<'male' | 'female' | 'other', string> = {
            'male': 'homme',
            'female': 'femme',
            'other': 'other'
          };

          setFormData({
            companyName: '',
            country: '',
            bceNumber: '',
            firstName: entity.first_name || '',
            lastName: entity.last_name || '',
            birthDate: entity.birth_date ? entity.birth_date.split('T')[0] : '', // Format date for input
            gender: entity.gender ? genderMap[entity.gender] || '' : '',
            birthPlace: entity.birth_place || '',
            personCountry: entity.country || '',
            email: entity.email || '',
            phone: entity.phone || '',
            addressLine1: entity.address_line1 || '',
            addressLine2: entity.address_line2 || '',
            city: entity.city || '',
            stateProvince: entity.state_province || '',
            postalCode: entity.postal_code || ''
          });
        }
      } else if (!entityDetailsQuery.isLoading && !entityDetailsQuery.error) {
        // Fallback to basic entity data if details query hasn't loaded yet
      if (entityType === 'entreprise') {
        setFormData({
            companyName: editingEntity.name || '',
            country: '',
            bceNumber: '',
          firstName: '',
          lastName: '',
          birthDate: '',
          gender: '',
          birthPlace: '',
          personCountry: '',
            email: editingEntity.email || '',
            phone: '',
            addressLine1: '',
            addressLine2: '',
            city: '',
            stateProvince: '',
            postalCode: ''
        });
      } else {
        setFormData({
          companyName: '',
          country: '',
          bceNumber: '',
            firstName: '',
            lastName: '',
            birthDate: '',
            gender: '',
            birthPlace: '',
            personCountry: '',
            email: editingEntity.email || '',
            phone: '',
            addressLine1: '',
            addressLine2: '',
            city: '',
            stateProvince: '',
            postalCode: ''
          });
        }
      }
    } else if (!isEditMode) {
      // Reset form for new entity
      setFormData({
        companyName: '',
        country: '',
        bceNumber: '',
        firstName: '',
        lastName: '',
        birthDate: '',
        gender: '',
        birthPlace: '',
        personCountry: '',
        email: '',
        phone: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        stateProvince: '',
        postalCode: ''
      });
    }
    setError(null);
  }, [isEditMode, editingEntity, entityType, entityDetailsQuery.data, entityDetailsQuery.isLoading, entityDetailsQuery.error]);

  // Reset mutation states when modal closes
  useEffect(() => {
    if (!isOpen) {
      createCompanyMutation.reset();
      createPersonMutation.reset();
      updateEntityMutation.reset();
      setError(null);
      setActiveTab('info'); // Reset to info tab when modal closes
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (isEditMode && editingEntity) {
      // Update existing entity
      if (entityType === 'entreprise') {
        updateEntityMutation.mutate(
          {
            params: {
              path: {
                organization_id: organizationId,
                id: editingEntity.id,
              },
            },
            body: {
              name: formData.companyName,
              country: formData.country,
              enterprise_number: formData.bceNumber || undefined,
              email: formData.email || undefined,
              phone: formData.phone || undefined,
              address_line1: formData.addressLine1 || undefined,
              address_line2: formData.addressLine2 || undefined,
              city: formData.city || undefined,
              state_province: formData.stateProvince || undefined,
              postal_code: formData.postalCode || undefined,
            } as unknown as Record<string, never>,
          },
          {
            onSuccess: () => {
              onSubmit();
              onClose();
              // Reset form
              setFormData({
                companyName: '',
                country: '',
                bceNumber: '',
                firstName: '',
                lastName: '',
                birthDate: '',
                gender: '',
                birthPlace: '',
                personCountry: '',
                email: '',
                phone: '',
                addressLine1: '',
                addressLine2: '',
                city: '',
                stateProvince: '',
                postalCode: ''
              });
              
              queryClient.invalidateQueries({ 
                queryKey: ['entity', editingEntity?.id],
              });
            },
            onError: (err) => {
              setError('Erreur lors de la modification de l\'entreprise');
              console.error('Error updating company:', err);
            },
          }
        );
      } else {
        // Map gender to API format
        const genderMap: Record<string, 'male' | 'female' | 'other'> = {
          'homme': 'male',
          'femme': 'female',
          'other': 'other'
        };

        updateEntityMutation.mutate(
          {
            params: {
              path: {
                organization_id: organizationId,
                id: editingEntity.id,
              },
            },
            body: {
              first_name: formData.firstName,
              last_name: formData.lastName,
              country: formData.personCountry,
              birth_date: formData.birthDate || undefined,
              gender: formData.gender ? (genderMap[formData.gender] || undefined) : undefined,
              birth_place: formData.birthPlace || undefined,
              email: formData.email || undefined,
              phone: formData.phone || undefined,
              address_line1: formData.addressLine1 || undefined,
              address_line2: formData.addressLine2 || undefined,
              city: formData.city || undefined,
              state_province: formData.stateProvince || undefined,
              postal_code: formData.postalCode || undefined,
            } as unknown as Record<string, never>,
          },
          {
            onSuccess: () => {
              onSubmit();
              onClose();
              // Reset form
              setFormData({
                companyName: '',
                country: '',
                bceNumber: '',
                firstName: '',
                lastName: '',
                birthDate: '',
                gender: '',
                birthPlace: '',
                personCountry: '',
                email: '',
                phone: '',
                addressLine1: '',
                addressLine2: '',
                city: '',
                stateProvince: '',
                postalCode: ''
              });
            },
            onError: (err) => {
              setError('Erreur lors de la modification de la personne');
              console.error('Error updating person:', err);
            },
          }
        );
      }
      return;
    }
    
    if (entityType === 'entreprise') {
      createCompanyMutation.mutate(
        {
          params: {
            path: { organization_id: organizationId },
          },
          body: {
        name: formData.companyName,
        country: formData.country,
            enterprise_number: formData.bceNumber || undefined,
            email: formData.email || undefined,
            phone: formData.phone || undefined,
            address_line1: formData.addressLine1 || undefined,
            address_line2: formData.addressLine2 || undefined,
            city: formData.city || undefined,
            state_province: formData.stateProvince || undefined,
            postal_code: formData.postalCode || undefined,
          } as unknown as { name: string; country: string; [key: string]: unknown },
        },
        {
          onSuccess: (data) => {
            onSubmit();
            onClose();
            // Reset form
            setFormData({
              companyName: '',
              country: '',
              bceNumber: '',
              firstName: '',
              lastName: '',
              birthDate: '',
              gender: '',
              birthPlace: '',
              personCountry: '',
              email: '',
              phone: '',
              addressLine1: '',
              addressLine2: '',
              city: '',
              stateProvince: '',
              postalCode: ''
            });

            // Invalidate all entity queries for this organization (both company and person)
            // This ensures the list refetches when user navigates back
            queryClient.invalidateQueries({ 
              queryKey: ['entities', organizationId],
              refetchType: 'active' // Only refetch active queries (when component is mounted)
            });
            
            // Navigate immediately - invalidation will ensure refetch when user comes back
            navigate(`/${organizationId}/entity/${data.id}`);
          },
          onError: (err) => {
            setError('Erreur lors de la création de l\'entreprise');
            console.error('Error creating company:', err);
          },
        }
      );
    } else {
      // Map gender to API format
      const genderMap: Record<string, 'male' | 'female' | 'other'> = {
        'homme': 'male',
        'femme': 'female',
        'other': 'other'
      };

      createPersonMutation.mutate(
        {
          params: {
            path: { organization_id: organizationId },
          },
          body: {
            first_name: formData.firstName,
            last_name: formData.lastName,
            country: formData.personCountry,
            birth_date: formData.birthDate || undefined,
            gender: formData.gender ? (genderMap[formData.gender] || undefined) : undefined,
            birth_place: formData.birthPlace || undefined,
            email: formData.email || undefined,
            phone: formData.phone || undefined,
            address_line1: formData.addressLine1 || undefined,
            address_line2: formData.addressLine2 || undefined,
            city: formData.city || undefined,
            state_province: formData.stateProvince || undefined,
            postal_code: formData.postalCode || undefined,
          } as unknown as { first_name: string; last_name: string; country: string; [key: string]: unknown },
        },
        {
          onSuccess: (data) => {
            onSubmit();
            onClose();
    // Reset form
      setFormData({
        companyName: '',
        country: '',
        bceNumber: '',
        firstName: '',
        lastName: '',
        birthDate: '',
        gender: '',
        birthPlace: '',
        personCountry: '',
        email: '',
              phone: '',
              addressLine1: '',
              addressLine2: '',
              city: '',
              stateProvince: '',
              postalCode: ''
            });

            // Invalidate all entity queries for this organization (both company and person)
            // This ensures the list refetches when user navigates back
            queryClient.invalidateQueries({ 
              queryKey: ['entities', organizationId],
              refetchType: 'active' // Only refetch active queries (when component is mounted)
            });
            
            // Navigate immediately - invalidation will ensure refetch when user comes back
            navigate(`/${organizationId}/entity/${data.id}`);
          },
          onError: (err) => {
            setError('Erreur lors de la création de la personne');
            console.error('Error creating person:', err);
          },
        }
      );
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            {entityType === 'entreprise' ? (
              <Building2 className="h-6 w-6 text-blue-600" />
            ) : (
              <User className="h-6 w-6 text-blue-600" />
            )}
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditMode 
                ? (entityType === 'entreprise' ? 'Modifier Entreprise' : 'Modifier Personne Physique')
                : (entityType === 'entreprise' ? 'Nouvelle Entreprise' : 'Nouvelle Personne Physique')
              }
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6">
          <nav className="flex space-x-4" aria-label="Tabs">
            <button
              type="button"
              onClick={() => setActiveTab('info')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'info'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <FileText className="h-4 w-4" />
                <span>Informations</span>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('address')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'address'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <MapPin className="h-4 w-4" />
                <span>Adresse</span>
                <span className="text-xs text-gray-400 font-normal">(optionnel)</span>
              </div>
            </button>
          </nav>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Loading entity details */}
          {isEditMode && entityDetailsQuery.isLoading && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <p className="text-blue-800 text-sm">Chargement des détails de l'entité...</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {(error || createCompanyMutation.error || createPersonMutation.error || updateEntityMutation.error || entityDetailsQuery.error) && (
            <ErrorMessage 
              error={error || createCompanyMutation.error || createPersonMutation.error || updateEntityMutation.error || entityDetailsQuery.error} 
              className="mb-4" 
            />
          )}

          {/* Success Message */}
          {(createCompanyMutation.isSuccess || createPersonMutation.isSuccess || updateEntityMutation.isSuccess) && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 text-sm">
                {isEditMode 
                  ? (entityType === 'entreprise' ? 'Entreprise modifiée avec succès' : 'Personne modifiée avec succès')
                  : (entityType === 'entreprise' ? 'Entreprise créée avec succès' : 'Personne créée avec succès')
                }
              </p>
            </div>
          )}

          {/* Info Tab Content */}
          {activeTab === 'info' && (
            <div className="space-y-4">
          {entityType === 'entreprise' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom de l'entreprise *
                </label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  disabled={isEditMode && entityDetailsQuery.isLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pays *
                </label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  disabled={isEditMode && entityDetailsQuery.isLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  required
                >
                  {
                    countries.map((country) => (
                      <option value={country.code}>{country.name}</option>
                    ))
                  }
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Numéro BCE *
                </label>
                <input
                  type="text"
                  name="bceNumber"
                  value={formData.bceNumber}
                  onChange={handleChange}
                  placeholder="0123.456.789"
                  disabled={isEditMode && entityDetailsQuery.isLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  required
                />
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prénom *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    disabled={isEditMode && entityDetailsQuery.isLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    disabled={isEditMode && entityDetailsQuery.isLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date de naissance *
                </label>
                <input
                  type="date"
                  name="birthDate"
                  value={formData.birthDate}
                  onChange={handleChange}
                  disabled={isEditMode && entityDetailsQuery.isLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Genre *
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  disabled={isEditMode && entityDetailsQuery.isLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  required
                >
                  <option value="">Sélectionner</option>
                  <option value="homme">Homme</option>
                  <option value="femme">Femme</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lieu de naissance *
                </label>
                <input
                  type="text"
                  name="birthPlace"
                  value={formData.birthPlace}
                  onChange={handleChange}
                  disabled={isEditMode && entityDetailsQuery.isLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pays *
                </label>
                <select
                  name="personCountry"
                  value={formData.personCountry}
                  onChange={handleChange}
                  disabled={isEditMode && entityDetailsQuery.isLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  required
                >
                  {
                    countries.map((country) => (
                      <option value={country.code}>{country.name}</option>
                    ))
                  }
                </select>
              </div>
            </>
          )}

          {/* Common fields */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled={isEditMode && entityDetailsQuery.isLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Téléphone *
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+33 1 23 45 67 89"
              disabled={isEditMode && entityDetailsQuery.isLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              required
            />
          </div>

            </div>
          )}

          {/* Address Tab Content */}
          {activeTab === 'address' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Optionnel :</strong> Vous pouvez renseigner l'adresse de l'entité. Tous les champs sont facultatifs.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ligne d'adresse 1
                </label>
                <input
                  type="text"
                  name="addressLine1"
                  value={formData.addressLine1}
                  onChange={handleChange}
                  disabled={isEditMode && entityDetailsQuery.isLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Rue, numéro"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ligne d'adresse 2
                </label>
                <input
                  type="text"
                  name="addressLine2"
                  value={formData.addressLine2}
                  onChange={handleChange}
                  disabled={isEditMode && entityDetailsQuery.isLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Complément d'adresse (optionnel)"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ville
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    disabled={isEditMode && entityDetailsQuery.isLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Ville"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Code postal
                  </label>
                  <input
                    type="text"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleChange}
                    disabled={isEditMode && entityDetailsQuery.isLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Code postal"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  État/Province
                </label>
                <input
                  type="text"
                  name="stateProvince"
                  value={formData.stateProvince}
                  onChange={handleChange}
                  disabled={isEditMode && entityDetailsQuery.isLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="État ou province (optionnel)"
                />
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={
                (isEditMode && entityDetailsQuery.isLoading) || 
                createCompanyMutation.isPending || 
                createPersonMutation.isPending ||
                updateEntityMutation.isPending
              }
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {(createCompanyMutation.isPending || createPersonMutation.isPending || updateEntityMutation.isPending) ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>{isEditMode ? 'Sauvegarde...' : 'Création...'}</span>
                </>
              ) : (isEditMode && entityDetailsQuery.isLoading) ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Chargement...</span>
                </>
              ) : (
                <span>
              {isEditMode 
                ? 'Sauvegarder les modifications'
                : `Créer ${entityType === 'entreprise' ? "l'entreprise" : 'la personne'}`
              }
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEntityModal;